<script>
import {
  CheckmarkCircle02Icon,
  Copy01Icon,
  PencilEdit02Icon,
} from '@hugeicons/core-free-icons';
import Avatar from 'next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import InboxChannelGlyph from 'dashboard/routes/dashboard/settings/inbox/components/InboxChannelGlyph.vue';

export default {
  components: {
    Avatar,
    InboxChannelGlyph,
    NextButton,
    HugeIcon,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    channelType: {
      type: String,
      default: '',
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['updateName'],
  data() {
    return {
      CheckmarkCircle02Icon,
      Copy01Icon,
      PencilEdit02Icon,
      isEditingName: false,
      firstName: '',
      lastName: '',
    };
  },
  computed: {
    contactReferenceId() {
      return this.contact.identifier || this.contact.id || '';
    },
    contactChannelInboxes() {
      const contactInboxes =
        this.contact.contact_inboxes || this.contact.contactInboxes || [];
      const inboxes = contactInboxes
        .map(contactInbox => contactInbox?.inbox)
        .filter(Boolean);

      if (inboxes.length) {
        return inboxes.filter(
          (inbox, index, allInboxes) =>
            index ===
            allInboxes.findIndex(
              item =>
                item.id === inbox.id ||
                (item.channel_type === inbox.channel_type &&
                  item.name === inbox.name)
            )
        );
      }

      return this.channelType ? [{ channel_type: this.channelType }] : [];
    },
  },
  watch: {
    'contact.name': {
      immediate: true,
      handler() {
        if (!this.isEditingName) {
          this.setNameFields();
        }
      },
    },
  },
  methods: {
    splitName(fullName = '') {
      const nameParts = fullName.trim().split(/\s+/).filter(Boolean);

      if (!nameParts.length) {
        return {
          firstName: '',
          lastName: '',
        };
      }

      if (nameParts.length === 1) {
        return {
          firstName: nameParts[0],
          lastName: '',
        };
      }

      return {
        firstName: nameParts.slice(0, -1).join(' '),
        lastName: nameParts.at(-1) || '',
      };
    },
    setNameFields() {
      const firstName = this.contact.first_name || this.contact.firstName || '';
      const lastName = this.contact.last_name || this.contact.lastName || '';

      if (firstName || lastName) {
        this.firstName = firstName;
        this.lastName = lastName;
        return;
      }

      const splitName = this.splitName(this.contact.name || '');
      this.firstName = splitName.firstName;
      this.lastName = splitName.lastName;
    },
    async copyName() {
      if (!this.contact.name) {
        return;
      }

      await copyTextToClipboard(this.contact.name);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    startEditingName() {
      this.setNameFields();
      this.isEditingName = true;
      this.$nextTick(() => {
        this.$refs.firstNameInput?.focus();
        this.$refs.firstNameInput?.select?.();
      });
    },
    cancelEditingName() {
      this.isEditingName = false;
      this.setNameFields();
    },
    saveName() {
      if (!this.isEditingName) {
        return;
      }

      const updatedName = [this.firstName.trim(), this.lastName.trim()]
        .filter(Boolean)
        .join(' ');

      this.isEditingName = false;

      if (!updatedName || updatedName === (this.contact.name || '').trim()) {
        this.setNameFields();
        return;
      }

      this.$emit('updateName', updatedName);
    },
  },
};
</script>

<template>
  <div class="relative w-full text-left rtl:text-right">
    <div class="flex w-full flex-col">
      <div class="flex min-w-0 items-center gap-2 px-2.5 py-1.5">
        <Avatar
          v-if="showAvatar"
          :src="contact.thumbnail"
          :name="contact.name"
          :status="contact.availability_status"
          :size="40"
          hide-offline-status
          rounded-full
        />
        <div class="min-w-0 flex-1">
          <div v-if="isEditingName" class="flex min-w-0 items-center gap-2">
            <input
              ref="firstNameInput"
              v-model="firstName"
              type="text"
              :placeholder="$t('CONTACTS_LAYOUT.NEW_CONTACT_PANEL.FIRST_NAME')"
              class="reset-base h-[30px] min-w-0 flex-1 rounded-xl border border-n-weak bg-n-surface-1 px-3 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand"
              @keydown.enter.prevent="saveName"
              @keydown.esc.prevent="cancelEditingName"
            />
            <input
              v-model="lastName"
              type="text"
              :placeholder="$t('CONTACTS_LAYOUT.NEW_CONTACT_PANEL.LAST_NAME')"
              class="reset-base h-[30px] min-w-0 flex-1 rounded-xl border border-n-weak bg-n-surface-1 px-3 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand"
              @keydown.enter.prevent="saveName"
              @keydown.esc.prevent="cancelEditingName"
            />
            <NextButton
              solid
              teal
              xs
              class="!h-[30px] !w-[30px] !rounded-xl !px-0"
              :title="$t('LIFECYCLE_STAGES.SETUP.SAVE')"
              @click="saveName"
            >
              <template #icon>
                <HugeIcon
                  :icon="CheckmarkCircle02Icon"
                  :size="16"
                  class="shrink-0"
                />
              </template>
            </NextButton>
          </div>
          <div
            v-else
            class="group/name flex min-w-0 items-start justify-between gap-2"
          >
            <div class="min-w-0 flex-1">
              <h3
                class="my-0 max-w-full min-w-0 break-words text-base font-bold leading-5 text-n-slate-12"
              >
                {{ contact.name || $t('CONTACT_PANEL.NOT_AVAILABLE') }}
              </h3>
              <p
                v-if="contactReferenceId"
                class="mt-0.5 text-xs leading-4 text-n-slate-10"
              >
                {{ $t('CONTACT_PANEL.REFERENCE_ID') }}: {{ contactReferenceId }}
              </p>
            </div>
            <div
              class="flex shrink-0 items-center gap-0.5 self-start opacity-0 transition-opacity group-hover/name:opacity-100"
            >
              <NextButton
                v-if="contact.name"
                ghost
                xs
                slate
                :title="$t('CONVERSATION.CONTEXT_MENU.COPY')"
                @click="copyName"
              >
                <template #icon>
                  <HugeIcon :icon="Copy01Icon" :size="16" class="shrink-0" />
                </template>
              </NextButton>
              <NextButton
                ghost
                xs
                slate
                :title="$t('EDIT_CONTACT.BUTTON_LABEL')"
                @click="startEditingName"
              >
                <template #icon>
                  <HugeIcon
                    :icon="PencilEdit02Icon"
                    :size="16"
                    class="shrink-0"
                  />
                </template>
              </NextButton>
            </div>
          </div>
        </div>
      </div>
      <div
        class="flex w-full items-center justify-between gap-2 border-b border-n-weak px-2.5 py-1.5"
      >
        <div class="flex min-w-0 items-center gap-1.5">
          <span
            v-for="inbox in contactChannelInboxes"
            :key="inbox.id || `${inbox.channel_type}-${inbox.name || ''}`"
            class="inline-flex size-[18px] shrink-0 items-center justify-center text-n-slate-11"
          >
            <InboxChannelGlyph :inbox="inbox" size="xs" />
          </span>
        </div>
        <span class="shrink-0 text-sm font-medium text-n-slate-12">
          {{ $t('CONTACT_PANEL.CHANNELS') }}
        </span>
      </div>
    </div>
  </div>
</template>
